import { useEffect, useState } from "react";
import { Check, X, Eye, Flag } from "lucide-react";
import { api, formatApiError, formatTL, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DRONE_STATUS, SLOT_LABEL, openReceipt } from "@/lib/shootOptions";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const FILTERS = {
  action: { label: "İşlem bekleyen", st: ["PENDING_APPROVAL", "RECEIPT_UPLOADED"] },
  payment: { label: "Ödeme bekleyen", st: ["AWAITING_PAYMENT", "DATE_REJECTED"] },
  approved: { label: "Kesinleşen", st: ["APPROVED"] },
  all: { label: "Tümü", st: null },
};

export const SettingsCard = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const save = async () => {
    try { await api.put("/admin/settings", { ...s, drone_extra_price: Number(s.drone_extra_price), vat_rate: Number(s.vat_rate) }); toast.success("Ayarlar kaydedildi"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Card className="p-4 grid md:grid-cols-[140px_120px_1fr_auto] gap-3 items-end" data-testid="drone-settings-card">
      <div><Label>Ek çekim ücreti (TL)</Label><Input data-testid="drone-price-input" type="number" min="0" value={s.drone_extra_price} onChange={(e) => setS({ ...s, drone_extra_price: e.target.value })} /></div>
      <div><Label>KDV %</Label><Input data-testid="drone-vat-input" type="number" min="0" max="100" value={s.vat_rate} onChange={(e) => setS({ ...s, vat_rate: e.target.value })} /></div>
      <div><Label>Ödeme bilgisi (IBAN vb.)</Label><Input data-testid="drone-payment-info-input" value={s.payment_info} onChange={(e) => setS({ ...s, payment_info: e.target.value })} /></div>
      <Button data-testid="drone-settings-save" onClick={save}>Kaydet</Button>
    </Card>
  );
};

const Row = ({ d, run, reject }) => {
  const st = DRONE_STATUS[d.status];
  const b = (id, label, icon, onClick, cls = "") => <Button size="sm" variant="outline" className={cls} data-testid={`${id}-${d.id}`} onClick={onClick}>{icon}{label}</Button>;
  return (
    <Card data-testid={`admin-drone-row-${d.id}`} className="p-4 flex flex-col md:flex-row md:items-center gap-3">
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold">{d.office_name}</span><span className="text-xs font-mono text-slate-500">{d.office_code}</span>
          <Badge variant="outline" className={st.cls} data-testid={`admin-drone-status-${d.id}`}>{st.label}</Badge>
          <Badge variant="outline">{d.is_free ? "Paket hakkı" : `${formatTL(d.total)} · ${d.reference_code}`}</Badge>
        </div>
        <div className="text-xs text-slate-500">{formatDate(d.date)} · {SLOT_LABEL[d.slot]} · {d.address} · {d.requested_by_name}</div>
      </div>
      <div className="flex gap-2 flex-wrap">
        {d.status === "PENDING_APPROVAL" && <>{b("admin-drone-approve", "Tarihi onayla", <Check className="w-4 h-4 mr-1" />, () => run(d, "approve-date", "Tarih onaylandı"), "text-emerald-700")}{b("admin-drone-reject", "Reddet", <X className="w-4 h-4 mr-1" />, () => reject(d, "reject-date"), "text-red-700")}</>}
        {d.receipt && b("admin-drone-receipt", "Dekont", <Eye className="w-4 h-4 mr-1" />, () => openReceipt(`/admin/drone-requests/${d.id}/receipt`))}
        {d.status === "RECEIPT_UPLOADED" && <>{b("admin-drone-confirm-pay", "Ödemeyi onayla", <Check className="w-4 h-4 mr-1" />, () => run(d, "confirm-payment", "Ödeme onaylandı"), "text-emerald-700")}{b("admin-drone-reject-pay", "Dekontu reddet", <X className="w-4 h-4 mr-1" />, () => reject(d, "reject-payment"), "text-red-700")}</>}
        {d.status === "APPROVED" && b("admin-drone-complete", "Tamamlandı", <Flag className="w-4 h-4 mr-1" />, () => run(d, "complete", "Çekim tamamlandı"))}
      </div>
    </Card>
  );
};

export default function AdminDronePage() {
  const { hasPerm } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("action");
  const [rej, setRej] = useState(null);
  const [reason, setReason] = useState("");
  const load = () => api.get("/admin/drone-requests").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const run = async (d, action, msg, body) => {
    try { await api.post(`/admin/drone-requests/${d.id}/${action}`, body); toast.success(msg); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const st = FILTERS[filter].st;
  const shown = st ? items.filter((d) => st.includes(d.status)) : items;

  return (
    <div className="space-y-6" data-testid="admin-drone-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Drone Talepleri</h1>
        <p className="text-slate-500 text-sm mt-1">Ofislerden gelen drone rezervasyonlarını onaylayın, dekontları kontrol edin.</p>
      </div>
      {hasPerm("PACKAGES_MANAGE") && <SettingsCard />}
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>{Object.entries(FILTERS).map(([k, f]) => <TabsTrigger key={k} value={k} data-testid={`admin-drone-tab-${k}`}>{f.label} ({f.st ? items.filter((d) => f.st.includes(d.status)).length : items.length})</TabsTrigger>)}</TabsList>
      </Tabs>
      <div className="space-y-2">
        {shown.length === 0 ? <Card className="p-8 text-center text-sm text-slate-500">Kayıt yok</Card>
          : shown.map((d) => <Row key={d.id} d={d} run={run} reject={(x, a) => { setRej({ d: x, a }); setReason(""); }} />)}
      </div>
      <Dialog open={!!rej} onOpenChange={(o) => !o && setRej(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{rej?.a === "reject-date" ? "Tarihi reddet" : "Dekontu reddet"}</DialogTitle></DialogHeader>
          <Textarea data-testid="admin-drone-reject-reason" placeholder="Ofise iletilecek not (isteğe bağlı)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setRej(null)}>İptal</Button>
            <Button data-testid="admin-drone-reject-confirm" className="bg-red-600 hover:bg-red-700" onClick={() => { run(rej.d, rej.a, "Reddedildi", { reason: reason || null }); setRej(null); }}>Reddet</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
